import React from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, XCircle, Loader2, Calendar, Building, FileSignature } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEsignPage() {
  const { esignId } = useParams();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!esignId) { setError('Missing certificate ID'); setLoading(false); return; }
    // Use plain axios (no auth) so this page is truly public.
    axios.get(`${BACKEND_URL}/api/verify/${esignId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This certificate is not on record.'))
      .finally(() => setLoading(false));
  }, [esignId]);

  const primary = data?.firm_primary_color || '#1A3A5C';
  const accent = data?.firm_accent_color || '#2ECC9A';

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: `linear-gradient(135deg, ${primary}08 0%, ${accent}12 100%)` }}
      data-testid="verify-page"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl">
        {loading && (
          <div className="flex flex-col items-center gap-3 py-8" data-testid="verify-loading">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: accent }} />
            <div className="text-sm text-slate-500">Verifying certificate…</div>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-3 py-8" data-testid="verify-not-found">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-7 w-7 text-red-600" />
            </div>
            <div className="text-lg font-semibold text-red-800">Certificate not found</div>
            <div className="text-center text-xs text-slate-500 max-w-xs">{error}</div>
            <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 font-mono text-[10px] text-slate-500">
              Certificate ID: {esignId}
            </div>
          </div>
        )}

        {!loading && data && (
          <div data-testid="verify-success">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: `${accent}20` }}>
                {data.status === 'signed' ? (
                  <CheckCircle2 className="h-7 w-7" style={{ color: accent }} />
                ) : (
                  <FileSignature className="h-7 w-7" style={{ color: primary }} />
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: accent }}>
                  {data.firm_name}
                </div>
                <div className="mt-0.5 font-serif text-2xl font-bold" style={{ color: primary }}>
                  {data.status === 'signed' ? 'Genuine signed document' : `Status: ${data.status}`}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Document</div>
              <div className="text-sm font-semibold" style={{ color: primary }} data-testid="verify-title">
                {data.title}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                Matter <span className="font-mono">{data.matter_ref}</span>
                {data.source_type === 'pdf' && ' · uploaded PDF'}
              </div>
            </div>

            {data.status === 'signed' && (
              <div className="mt-3 grid gap-2 text-xs">
                <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                  <FileSignature className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="text-slate-500">Signed by</span>
                  <span className="ml-auto font-mono font-medium" style={{ color: primary }} data-testid="verify-signer">
                    {data.signer_name_redacted || '—'}
                    {data.signer_email_redacted && (
                      <span className="ml-1 font-normal text-slate-500">({data.signer_email_redacted})</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                  <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="text-slate-500">Signed on</span>
                  <span className="ml-auto font-medium" style={{ color: primary }} data-testid="verify-signed-at">
                    {data.signed_at ? new Date(data.signed_at).toLocaleString('en-AU') : '—'}
                  </span>
                </div>
                {data.reminder_count > 0 && (
                  <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <span className="text-slate-500">Reminders sent</span>
                    <span className="ml-auto font-medium" style={{ color: primary }}>{data.reminder_count}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex items-start gap-2 rounded-md p-3" style={{ background: `${accent}10` }}>
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accent }} />
              <div className="text-[11px] leading-relaxed text-slate-600">
                This certificate was issued by <b style={{ color: primary }}>{data.firm_name}</b> using
                ImmiPro Native eSign. Personal information is intentionally partially redacted on this
                public verify page — the full audit trail (including signer IP, user-agent, and captured
                signature image) is only visible from inside the issuing firm's workspace.
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-1 text-[10px] text-slate-400">
              <Building className="h-3 w-3" />
              <span className="font-mono">{data.esign_id}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
